import React, { useCallback } from "react";
import ReactImageZoom from "react-image-zoom";
import PropTypes from "prop-types";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";

// Karsten
import MaskAndValid from "utils/maskAndValid";
import NoImageImg from "assets/img/noImage.png";
import { IsMobile } from "utils/is-mobile";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
}));

const ImageModal = ({ open, onClose, imagePreviewTitle, ...props }) => {
  const classes = useStyles();
  const { src, ...imageProps } = props.imageProps;
  const isMobile = IsMobile();

  const reactImageProps = {
    ...imageProps,
    img: src,
    width: isMobile ? 280 : 500,
    zoomWidth: isMobile ? 280 : 500,
    zoomPosition: "original",
    zoomStyle: "cursor: zoom-in;",
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <Box
            display="flex"
            width={isMobile ? "80%" : "100%"}
            justifyContent={isMobile ? "space-around" : "space-between"}
            marginBottom="1rem"
          >
            <Typography component="h2">{imagePreviewTitle || ""}</Typography>
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: isMobile ? 0 : "1rem",
                cursor: "pointer",
              }}
              onClick={onClose}
            >
              <Typography variant="caption">Fechar</Typography>
              <CloseIcon onClick={onClose} style={{ marginLeft: "0.5rem" }} />
            </Box>
          </Box>
          <ReactImageZoom {...reactImageProps} />
        </div>
      </Fade>
    </Modal>
  );
};

const Image = ({ imageProps, style, onMouseOver, onMouseOut, className }) => {
  return (
    <img
      loading="lazy"
      alt="..."
      {...imageProps}
      style={style}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      className={className}
    />
  );
};

function ImagePlaceholder({
  src,
  alt,
  imageErrorUrl,
  width,
  widthOnError,
  imagePreviewTitle,
  ...imageProps
}) {
  const [defaultImage] = React.useState(imageErrorUrl || NoImageImg);
  const isValidImage = !MaskAndValid.isNullOrEmpty(src);
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getDefaultSrc = useCallback(
    (event) => {
      event.target.src = defaultImage;
    },
    [defaultImage]
  );

  const defaultProps = {
    src: isValidImage ? src : defaultImage,
    onError: getDefaultSrc,
    alt,
    width: isValidImage ? width : widthOnError,
  };

  return (
    <>
      <Box display="flex" onClick={isValidImage ? handleOpen : null}>
        <Image
          imageProps={{ ...defaultProps, ...imageProps }}
          style={{ cursor: isValidImage ? "pointer" : "initial" }}
        />
      </Box>
      {open && (
        <ImageModal
          open={open}
          onClose={handleClose}
          imageProps={{ ...defaultProps, ...imageProps }}
          imagePreviewTitle={imagePreviewTitle}
        />
      )}
    </>
  );
}

ImagePlaceholder.propTypes = {
  /**
   * URL da Imagem que será exibida
   */
  src: PropTypes.string,
  /**
   * Texto alternativo para imagem. Util para acessibilidade. https://www.w3schools.com/TAGS/att_img_alt.asp
   */
  alt: PropTypes.string,
  /**
   * Imagem que sera exibida caso a imagem passada na prop 'src' nao funcione
   */
  imageErrorUrl: PropTypes.string,
  /**
   * Imagem que sera exibida caso a imagem passada na prop 'src' nao funcione
   */
  imageStyle: PropTypes.object,
  /**
   * Title que aparece na modal de visualização de imagem
   */
  imagePreviewTitle: PropTypes.string,
};

export default ImagePlaceholder;
